import { artDirectionPromptLines } from "../style/artDirection.js";
import { cutoutChromaKeyPromptLines, solidBackdropPromptLines, } from "./rasterBackdrop.js";
/**
 * F19 — Text baked into AI image pixels (Pulse `in_image` pattern).
 * Image models may garble spelling/diacritics; fidelity is not guaranteed.
 */
export function getLayerTextRender(layer) {
    const raw = layer.style?.textRender;
    return raw === "in_image" ? "in_image" : "overlay";
}
export function normalizeBakedText(data) {
    const title = data.title?.trim() || undefined;
    const bullets = (data.bullets ?? [])
        .map((b) => String(b).replace(/^•\s*/, "").trim())
        .filter(Boolean);
    return {
        title,
        bullets: bullets.length ? bullets : undefined,
    };
}
export function getLayerBakedText(layer) {
    const raw = layer.style?.bakedText;
    if (!raw || typeof raw !== "object")
        return {};
    const obj = raw;
    return normalizeBakedText({
        title: typeof obj.title === "string" ? obj.title : undefined,
        bullets: Array.isArray(obj.bullets) ? obj.bullets : undefined,
    });
}
export function setLayerBakedText(layer, baked) {
    return {
        ...layer,
        style: {
            ...layer.style,
            bakedText: normalizeBakedText(baked),
        },
    };
}
export function setLayerTextRender(layer, mode) {
    return {
        ...layer,
        style: {
            ...layer.style,
            textRender: mode,
        },
    };
}
/** Accessibility / export metadata for baked illustration layers. */
export function bakedTextAltText(baked) {
    const parts = [baked.title, ...(baked.bullets ?? [])].filter(Boolean);
    return parts.join(". ").slice(0, 500) || "Illustrazione con testo";
}
export function extractBakedTextFromSlide(slide) {
    const title = slide.layers
        .find((l) => l.type === "text" && l.textRole === "title")
        ?.content?.trim();
    const subtitle = slide.layers
        .find((l) => l.type === "text" && l.textRole === "subtitle")
        ?.content?.trim();
    const bullets = slide.layers
        .filter((l) => l.type === "text" && l.textRole === "bullet")
        .map((l) => l.content.replace(/^•\s*/, "").trim())
        .filter(Boolean);
    return normalizeBakedText({
        title: title || subtitle,
        bullets: bullets.length ? bullets : undefined,
    });
}
export function resolveBakedTextForLayer(layer, slide) {
    const fromLayer = getLayerBakedText(layer);
    if (fromLayer.title || (fromLayer.bullets?.length ?? 0) > 0)
        return fromLayer;
    return extractBakedTextFromSlide(slide);
}
export function composeBakedIllustrationPrompt(style, subject, baked, slideIndex, cutout, aspect, backdropHex) {
    const safe = style.legibility.safeZones[0];
    const cleanArea = safe
        ? `Reserve clean readable areas for typography (calm zones) near normalized x=${safe.x.toFixed(2)} y=${safe.y.toFixed(2)} w=${safe.w.toFixed(2)} h=${safe.h.toFixed(2)}.`
        : "Reserve calm clean areas for integrated legible typography.";
    const normalized = normalizeBakedText(baked);
    const title = normalized.title ?? "";
    const bullets = normalized.bullets ?? [];
    let textInstructions = "";
    if (title) {
        const escaped = title.replace(/"/g, '\\"');
        textInstructions = `Render exactly this headline as prominent, legible typography integrated into the artwork (bold sans-serif, strong contrast, correct spelling): "${escaped}".`;
    }
    if (bullets.length) {
        const lines = bullets.slice(0, 6).map((b) => `"${b.replace(/"/g, '\\"')}"`);
        textInstructions += ` Include these lines as legible bullet text in the illustration: ${lines.join(", ")}.`;
    }
    if (!textInstructions) {
        textInstructions =
            "Integrate short legible headline typography derived from the subject into the artwork.";
    }
    textInstructions +=
        " Keep typography integrated with the illustration; no separate floating text box or browser-style caption bar.";
    const lead = aspect === "1:1"
        ? "1:1 square ILLUSTRATION with text baked into the image pixels."
        : aspect === "2:3"
            ? "2:3 portrait ILLUSTRATION with text baked into the image pixels."
            : aspect === "3:2"
                ? "3:2 landscape ILLUSTRATION with text baked into the image pixels."
                : "16:9 presentation ILLUSTRATION with text baked into the image pixels.";
    const parts = [
        lead,
        `Visual mood / subject: ${subject}`,
        `Visual treatment: ${style.backgroundTreatment}`,
        `Palette mood: surface ${style.palette.surface}, accent ${style.palette.accent}`,
        ...artDirectionPromptLines(style, "content"),
        cleanArea,
        textInstructions,
        `Slide ${slideIndex + 1}.`,
    ];
    if (backdropHex) {
        parts.push(...solidBackdropPromptLines(backdropHex));
    }
    else if (cutout) {
        parts.push(...cutoutChromaKeyPromptLines());
    }
    return parts.join(" ");
}
export function isBakedIllustrationLayer(layer) {
    return layer.type === "image" && getLayerTextRender(layer) === "in_image";
}
